import {DisplayNameTypes, GenderKeyNameE, UserStatusesE} from '@/features/constants/constants'
import {
    CountrySchema,
    CountrySchemaV3,
    CountryWithLanguageSchema,
    LanguageSchema,
    LanguageSchemaV3
} from '@/features/country/services/country.schema'
import {CreditCardTypeE, SocialPlatformTypeE} from '@/features/guest-payments/types'
import {IdCardStatusesE} from '@/utilities/constants/user'
import {boolean, z} from 'zod'
import {UserRoleE} from '@/types'
import {PhotoSchema} from '@/features/host/services/host.schema'
import {AlertTypeE} from '../types'
import {GoalStatusesE} from '@/features/goal/types'

const UserStatuses = z.nativeEnum(UserStatusesE)
const GenderKeyNames = z.nativeEnum(GenderKeyNameE)

export enum GuestLevelStatusE {
    GUEST = 'guest',
    SILVER = 'silver',
    GOLD = 'gold',
    DIAMOND = 'diamond',
    ULTIMATE = 'ultimate'
}
export const GuestLevelStatusSchema = z.nativeEnum(GuestLevelStatusE)

export const ChatTopicsSchema = z.object({
    id: z.number(),
    name: z.string(),
    key_name: z.string(),
    is_popular: z.boolean()
})

export const ChatTopicsSchemaV3 = z.object({
    id: z.number(),
    name: z.string(),
    keyName: z.string(),
    isPopular: z.boolean()
})

export const FavoriteActivitiesSchema = z.object({
    id: z.number(),
    name: z.string(),
    key_name: z.string(),
    is_popular: z.boolean()
})

export const FavoriteActivitiesSchemaV3 = z.object({
    id: z.number(),
    name: z.string(),
    keyName: z.string(),
    isPopular: z.boolean()
})

export const SocialsSchema = z.object({
    id: z.number(),
    followers_amount: z.string(),
    label: z.string(),
    social_url: z.string().nullable().optional(),
    social_media: z.object({
        id: z.number(),
        key_name: z.string(),
        name: z.string()
    })
})

export const SocialsSchemaV3 = z.object({
    id: z.number(),
    followersAmount: z.string(),
    label: z.string(),
    socialUrl: z.string().nullable().optional(),
    socialMedia: z.object({
        id: z.number(),
        keyName: z.string(),
        name: z.string()
    })
})

export const SpokenLanguagesSchema = z.object({
    id: z.number(),
    name: z.string(),
    code: z.string(),
    is_translatable: z.boolean()
})

export const GenderSchema = z.object({
    id: z.number(),
    name: z.string(),
    key_name: GenderKeyNames
})

export const GenderSchemaV3 = z.object({
    id: z.number(),
    name: z.string(),
    keyName: GenderKeyNames
})

export const GuestTypeAttributesSchemaV3 = z.object({
    id: z.number(),
    ageVerifiedAt: z.union([z.date(), z.string()]).nullable(),
    anonymousTransactionToRetryId: z.number().nullable().optional(),
    microMorgiBalance: z.number(),
    levelStatus: GuestLevelStatusSchema,
    hasSupportedGoal: z.boolean(),
    topFansCount: z.number(),
    wish: z.object({id: z.number(), name: z.string(), keyName: z.string()}).optional().nullable(),
    hasEverPaid: z.boolean(),
    canOpenConnection: z.boolean(),
    canSendMessages: z.boolean(),
    hasNewCreditCard: z.boolean(),
    isFirstTransaction: z.boolean(),
    ultimateOffer: z.object({
        isUltimateStatusOfferAvailable: z.boolean(),
        endDate: z.union([z.date(), z.string()]).nullable()
    }),
    memoriesCount: z.number(),
    nextDollarAmount: z.number().nullable(),
    isTopFansBadgeVisible: z.boolean(),
    preferredPaymentMethod: z
        .object({
            platform: z.nativeEnum(CreditCardTypeE),
            systemName: z.string(),
            gatewayType: z.string(),
            gatewayHash: z.string().nullable(),
            last4: z.union([z.number().nullable(), z.string().nullable()])
        })
        .nullable()
        .optional(),
    cashbackOffer: z
        .object({
            isAvailableCashbackOffer: z.boolean(),
            endDate: z.union([z.date(), z.string()]).nullable()
        })
        .optional(),
    hasInvoicingProfile: boolean().optional(),
    followerCarouselFilters: z
        .object({
            leaderId: z.number(),
            filters: z.object({
                countries: z.array(CountryWithLanguageSchema),
                languages: z.array(LanguageSchema),
                genders: z.array(GenderSchema),
                fromAge: z.number().nullable(),
                toAge: z.number().nullable(),
                specialHostCategory: z
                    .array(
                        z.object({
                            value: z.number(),
                            label: z.string(),
                            data: z.object({
                                key_name: z.string(),
                                category: z.string()
                            })
                        })
                    )
                    .nullable()
                    .optional()
            })
        })
        .nullable()
        .optional(),
    isCountryRestricted: z.boolean().nullable()
})

export const GuestTypeAttributesSchema = z.object({
    id: z.number(),
    anonymous_transaction_to_retry_id: z.number().nullable().optional(),
    coupons: z.array(z.unknown()),
    has_converter_chat: z.boolean(),
    has_approved_transaction: z.boolean(),
    micro_morgi_balance: z.number(),
    total_coupons_got: z.number(),
    carousel_type: z.string(),
    level_status: z.string(),
    next_level_amount: z.number().nullable(),
    top_fans_count: z.number(),
    leaders_carousels_filters: z
        .object({
            leader_id: z.number(),
            filters: z.object({
                countries: z.array(CountryWithLanguageSchema),
                languages: z.array(LanguageSchema),
                genders: z.array(GenderSchema),
                fromAge: z.number().nullable(),
                toAge: z.number().nullable(),
                specialHostCategory: z
                    .array(
                        z.object({
                            value: z.number(),
                            label: z.string(),
                            data: z.object({
                                key_name: z.string(),
                                category: z.string()
                            })
                        })
                    )
                    .nullable()
                    .optional()
            })
        })
        .nullable(),
    has_supported_goal: z.boolean(),
    wish: z
        .array(z.object({id: z.number(), name: z.string(), key_name: z.string()}))
        .optional()
        .nullable(),
    has_ever_paid: z.boolean(),
    can_open_connection: z.boolean(),
    connections_opened_today: z.number(),
    can_send_messages: z.boolean(),
    messages_sent_today: z.number(),
    active_credit_card: z
        .object({
            cardType: z.string().nullable(),
            last4: z.number().nullable(),
            expDate: z.string().nullable()
        })
        .nullable(),
    has_new_credit_card: z.boolean(),
    is_first_transaction: z.boolean(),
    interested_in_gender: GenderSchema.nullable(),
    ultimate_offer: z.object({
        is_ultimate_status_offer_available: z.boolean(),
        end_date: z.union([z.date(), z.string()]).nullable()
    }),
    memories_count: z.number(),
    next_dollar_amount: z.number().nullable(),
    is_top_fans_badge_visible: z.boolean(),
    preferred_payment_method: z
        .object({
            platform: z.nativeEnum(CreditCardTypeE),
            system_name: z.string(),
            gateway_type: z.string(),
            gateway_hash: z.string().nullable(),
            last4: z.union([z.number().nullable(), z.string().nullable()])
        })
        .nullable()
        .optional(),
    show_feed_additions: z.boolean().optional(),
    cashback_offer: z
        .object({
            is_available_cashback_offer: z.boolean(),
            end_date: z.union([z.date(), z.string()]).nullable()
        })
        .optional(),
    has_invoicing_profile: boolean().optional()
})

export const FeaturesGuestsSchema = z.array(
    z.object({
        id: z.number(),
        type: z.enum(['leader', 'rookie', 'representative']),
        full_name: z.string(),
        username: z.string(),
        description: z.string().nullable(),
        is_online: z.boolean(),
        pubnub_uuid: z.string(),
        created_at: z.string(),
        chat_is_online: z.boolean(),
        has_verified_id: z.boolean(),
        spoken_languages: z.array(SpokenLanguagesSchema),
        gender: GenderSchema.nullable(),
        avatar: z
            .object({
                id: z.number(),
                created_at: z.string(),
                main: z.boolean(),
                path_location: z.string(),
                under_validation: z.boolean(),
                url: z.string().url(),
                user_id: z.number()
            })
            .nullable(),
        video: z
            .object({
                id: z.number(),
                user_id: z.number(),
                url: z.string(),
                path_location: z.string(),
                is_processed: z.number().optional(),
                created_at: z.string(),
                under_validation: z.boolean()
            })
            .nullable()
            .optional(),
        type_attributes: z.object({
            id: z.number(),
            level_status: z.string().optional(),
            interested_in_gender: GenderSchema.optional().nullable(),
            has_ever_paid: z.boolean().optional(),
            wish: z
                .array(
                    z.object({
                        id: z.number(),
                        name: z.string(),
                        key_name: z.string()
                    })
                )
                .nullable()
                .optional()
        }),
        chat_topics: z.array(ChatTopicsSchema),
        favorite_activities: z.array(FavoriteActivitiesSchema),
        //TODO fix when user object will be fix
        // socials: z.array(SocialsSchema)
        socials: z.any()
    })
)

export const HostsAgentSchema = z.object({
    id: z.number(),
    avatar: z
        .object({
            id: z.number(),
            user_id: z.number(),
            path_location: z.string(),
            main: z.boolean(),
            is_face_recognition_processed: z.boolean(),
            title: z.string().nullable(),
            content: z.string().nullable(),
            tags: z.string().nullable(),
            created_at: z.string(),
            updated_at: z.string(),
            url: z.string(),
            under_validation: z.boolean()
        })
        .nullable()
        .optional(),
    first_name: z.string(),
    last_name: z.string()
})

export const HostsAgentSchemaV3 = z.object({
    id: z.number().optional(),
    createdAt: z.string().nullable().optional(),
    deletedAt: z.string().nullable().optional(),
    invitationId: z.string().nullable().optional(),
    isSleazy: z.boolean().nullable().optional(),
    microMorgiBalance: z.number().nullable().optional(),
    morgiBalance: z.number().nullable().optional(),
    phoneNumber: z.number().nullable().optional(),
    phoneNumberCountry: z.number().nullable().optional(),
    withdrawalBalance: z.number().nullable().optional(),
    avatar: z
        .object({
            id: z.number(),
            userId: z.number(),
            pathLocation: z.string(),
            main: z.boolean(),
            isFaceRecognitionProcessed: z.boolean(),
            title: z.string().nullable(),
            content: z.string().nullable(),
            tags: z.string().nullable(),
            createdAt: z.string(),
            updatedAt: z.string(),
            url: z.string(),
            underValidation: z.boolean()
        })
        .nullable()
        .optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional()
})

export type HostsAgentTypeV3 = z.infer<typeof HostsAgentSchemaV3>

export const HostPhotos = z.object({
    id: z.number(),
    user_id: z.number(),
    url: z.string().nullable(),
    path_location: z.string().nullable(),
    main: z.boolean(),
    created_at: z.string(),
    under_validation: z.boolean()
})
export const FeaturedRookieSchema = z.object({
    id: z.number(),
    rookie_id: z.number(),
    featured_rookie_id: z.number(),
    full_name: z.string(),
    birth_date: z.string().nullable().optional(),
    username: z.string().optional(),
    position: z.number(),
    photos: z
        .array(
            z.object({
                id: z.number(),
                user_id: z.number(),
                url: z.string().nullable(),
                path_location: z.string().nullable(),
                main: z.boolean(),
                created_at: z.string(),
                under_validation: z.boolean()
            })
        )
        .optional(),
    path: z
        .object({
            id: z.number(),
            name: z.string(),
            prepend: z.string(),
            key_name: z.string(),
            is_subpath: z.boolean()
        })
        .nullable(),
    subpath: z
        .object({
            id: z.number(),
            name: z.string(),
            prepend: z.string(),
            key_name: z.string(),
            is_subpath: z.boolean()
        })
        .nullable()
        .optional(),
    expertise: z
        .object({
            id: z.number(),
            name: z.string(),
            key_name: z.string(),
            category: z.string(),
            created_at: z.string(),
            updated_at: z.string()
        })
        .nullable()
        .optional()
})
export const HostPathSchema = z.object({
    id: z.number(),
    name: z.string(),
    keyName: z.string(),
    isSubpath: z.boolean().optional(),
    prepend: z.string()
})
export const HostTypeAttributesSchemeV3 = z.object({
    isInfluencer: z.boolean().optional(),
    agent: HostsAgentSchemaV3.nullable().optional(),
    agentRevenueShare: z.string().optional().nullable(),
    minMorgiToOpenConnection: z.number().nullable().optional(),
    id: z.number(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    birthDate: z.string().optional().nullable(),
    untaxedMorgiBalance: z.number(),
    untaxedMicroMorgiBalance: z.number(),
    untaxedWithdrawalBalance: z.number(),
    morgiBalance: z.number(),
    microMorgiBalance: z.number(),
    withdrawalBalance: z.number(),
    revenueShare: z.union([z.string(), z.number()]),
    readMustDos: z.boolean().optional(), //DIEGO
    hasThankYous: z.boolean().optional(), //DIEGO
    participatedToCourse: z.boolean().optional(), //DIEGO
    specialType: z.string().nullable().optional(),
    limitApproachesPerDay: z.number(),
    todayApproaches: z.number(),
    followersCount: z.number(),
    phoneNumberCountry: z.string().nullable().optional(),
    phoneNumber: z.string().nullable().optional(),
    hasPastGoals: z.boolean(),
    paymentForFirstConnection: z.boolean().nullable().optional(),
    nickname: z.string().optional().nullable(),
    isVisibleAge: z.boolean().optional().nullable(),
    displayNameType: z.nativeEnum(DisplayNameTypes),
    photosCount: z.number().optional(), //DIEGO
    photos: z.array(HostPhotos).optional().nullable(), //DIEGO
    videos: z
        .array(
            z.object({
                id: z.number(),
                userId: z.number(),
                blurredUrl: z.string().nullable(),
                blurredPathLocation: z.string().nullable(),
                isProcessed: z.number().optional(),
                createdAt: z.string(),
                underValidation: z.boolean(),
                url: z.string().nullable(),
                pathLocation: z.string().nullable()
            })
        )
        .optional(), //DIEGO
    videosCount: z.number().optional(), //DIEGO
    country: CountrySchemaV3.nullable(),
    region: z.object({id: z.number().optional(), name: z.string()}).nullable(),
    city: z.object({id: z.number(), name: z.string(), countryId: z.number()}).nullable(),
    path: HostPathSchema.nullable(),
    subpath: z.string().nullable().optional(),
    // isWinner: z.boolean().nullable(),
    // converterRequest: z.null().optional(),
    // featuredLeaders: FeaturesGuestsSchema,
    referralMessage: z
        .object({
            id: z.number(),
            userId: z.number(),
            message: z.string()
        })
        .nullable()
        .optional(), //DIEGO
    // hasRepresentative: z.boolean().optional(),
    megaGiftsCount: z.number(),
    topFansInfos: z
        .object({
            completedCounter: z.number(),
            minAmountToReach: z.number(),
            pendingCounter: z.number(),
            groupId: z.number().nullable()
        })
        .nullable()
        .optional(),
    expertise: z
        .object({
            id: z.number(),
            keyName: z.string(),
            category: z.string(),
            name: z.string()
        })
        .nullable()
        .optional(),
    mediaPackagesCount: z.number().optional(),
    subscriptionPackage: z
        .object({
            id: z.number(),
            amount: z.number(),
            dollarAmount: z.number(),
            isDefault: z.boolean(),
            isVisible: z.boolean()
        })
        .nullable()
        .optional()
})

export const HostTypeAttributesScheme = z.object({
    is_influencer: z.boolean().optional(),
    agent: HostsAgentSchema.nullable().optional(),
    agent_revenue_share: z.string().optional().nullable(),
    min_morgi_to_open_connection: z.number().nullable().optional(),
    id: z.number(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    birth_date: z.string().optional().nullable(),
    is_converter: z.boolean(),
    untaxed_morgi_balance: z.number(),
    untaxed_micro_morgi_balance: z.number(),
    untaxed_withdrawal_balance: z.number(),
    morgi_balance: z.number(),
    micro_morgi_balance: z.number(),
    withdrawal_balance: z.number(),
    path_changes_count: z.number(),
    first_micromorgi_gift_leaders: z.number(),
    revenue_share: z.union([z.string(), z.number()]),
    read_must_dos: z.boolean(),
    has_thank_yous: z.boolean(),
    participated_to_course: z.boolean(),
    is_special: z.boolean().optional(),
    special_type: z.string().nullable().optional(),
    is_visible: z.boolean(),
    limit_approach_per_day: z.number(),
    today_approaches: z.number(),
    phone_number_country: z.string().nullable().optional(),
    phone_number: z.string().nullable().optional(),
    has_past_goals: z.boolean(),
    payment_for_first_connection: z.boolean().nullable().optional(),
    nickname: z.string().optional().nullable(),
    is_visible_age: z.boolean().optional().nullable(),
    display_name_type: z.nativeEnum(DisplayNameTypes),
    goals: z.array(
        z.object({
            id: z.number(),
            name: z.string(),
            slug: z.string(),
            details: z.string(),
            target_amount: z.string(),
            currency_type: z.string(),
            rookie_id: z.number(),
            start_date: z.string(),
            end_date: z.string(),
            thank_you_message: z.string(),
            cancelled_at: z.string().datetime().nullable(),
            cancelled_reason: z.string().nullable(),
            type_id: z.number(),
            status: z.nativeEnum(GoalStatusesE),
            proof_note: z.string(),
            created_at: z.string(),
            has_image_proof: z.boolean(),
            has_video_proof: z.boolean(),
            broadcast: z
                .object({
                    id: z.number(),
                    sender_id: z.number(),
                    representative_id: z.number().nullable(),
                    created_at: z.string(),
                    updated_at: z.string(),
                    is_goal: z.boolean(),
                    display_name: z.string(),
                    pivot: z.object({
                        goal_id: z.number(),
                        broadcast_id: z.number()
                    })
                })
                .nullable(),
            is_for_others: z.boolean(),
            radiobox: z.union([z.string(), z.null()]),
            leader_donations_amount: z.number(),
            is_saved: z.boolean(),
            media: z
                .array(
                    z.object({
                        id: z.number(),
                        goal_id: z.number(),
                        type: z.string(),
                        is_main: z.boolean(),
                        path_location: z.string(),
                        url: z.string()
                    })
                )
                .nullable(),
            proofs: z
                .array(
                    z.object({
                        id: z.number(),
                        goal_id: z.number(),
                        type: z.string(),
                        path_location: z.string(),
                        status: z.string(),
                        created_at: z.string(),
                        updated_at: z.string().nullable(),
                        declined_reason: z.string().nullable(),
                        admin_id: z.number().nullable(),
                        url: z.string()
                    })
                )
                .nullable(),
            type: z.object({
                id: z.number(),
                type: z.string(),
                min: z.number(),
                max: z.number(),
                currency_type: z.string(),
                duration_type: z.string(),
                duration_value: z.number(),
                is_active: z.number()
            }),
            donations_count: z.union([z.string(), z.number()]),
            rookie: z.object({
                id: z.number(),
                avatar: z
                    .object({
                        id: z.number(),
                        user_id: z.number(),
                        path_location: z.string(),
                        main: z.boolean(),
                        is_face_recognition_processed: z.boolean(),
                        title: z.string().nullable(),
                        content: z.string().nullable(),
                        tags: z.string().nullable(),
                        created_at: z.string(),
                        updated_at: z.string(),
                        url: z.string(),
                        under_validation: z.boolean()
                    })
                    .nullable(),
                gender: GenderSchema.nullable(),
                username: z.string(),
                full_name: z.string()
            }),
            goal_group: z
                .object({
                    id: z.number(),
                    user_id: z.number(),
                    key_name: z.string(),
                    goal_id: z.number(),
                    created_at: z.string(),
                    updated_at: z.string()
                })
                .nullable()
        })
    ),
    photos_count: z.number(),
    photos: z.array(HostPhotos).nullable().optional(),
    videos: z.array(
        z.object({
            id: z.number(),
            user_id: z.number(),
            blurred_url: z.string().nullable(),
            blurred_path_location: z.string().nullable(),
            is_processed: z.number().optional(),
            created_at: z.string(),
            under_validation: z.boolean(),
            url: z.string().nullable(),
            path_location: z.string().nullable()
        })
    ),
    videos_count: z.number(),
    country: CountrySchema.nullable(),
    region: z.object({id: z.number().optional(), name: z.string()}).nullable(),
    city: z.object({id: z.number(), name: z.string(), country_id: z.number()}).nullable(),
    path: z
        .object({
            id: z.number(),
            name: z.string(),
            key_name: z.string(),
            is_subpath: z.boolean(),
            prepend: z.string()
        })
        .nullable(),
    subpath: z.string().nullable().optional(),
    is_winner: z.boolean().nullable(),
    converter_request: z.null(),
    featured_leaders: FeaturesGuestsSchema,
    referral_message: z
        .object({
            id: z.number(),
            user_id: z.number(),
            message: z.string()
        })
        .nullable(),
    has_representative: z.boolean(),
    mega_gifts_count: z.number(),
    top_fans_infos: z
        .object({
            completed_counter: z.number(),
            min_amount_to_reach: z.number(),
            pending_counter: z.number(),
            group_id: z.number().nullable()
        })
        .optional(),
    expertise: z
        .object({
            id: z.number(),
            key_name: z.string(),
            category: z.string(),
            name: z.string()
        })
        .nullable()
        .optional(),
    media_packages_count: z.number().optional(),
    subscription_package: z
        .object({
            id: z.number(),
            amount: z.number(),
            dollar_amount: z.number(),
            is_default: z.boolean(),
            is_visible: z.boolean()
        })
        .nullable()
        .optional(),
    featured_rookies: z.array(FeaturedRookieSchema).optional(),
    saved_count: z.number().optional()
})

export const IdVerifiedSchema = z.union([
    z.object({
        card_id_status: z.nativeEnum(IdCardStatusesE),
        card_id_reason: z.string().nullable(),
        front: z.object({
            status: z.nativeEnum(IdCardStatusesE),
            url: z.string(),
            path_location: z.string(),
            reason: z.string().nullable()
        }),
        back: z.object({
            status: z.nativeEnum(IdCardStatusesE),
            url: z.string(),
            path_location: z.string(),
            reason: z.string().nullable()
        }),
        selfie: z.object({
            status: z.nativeEnum(IdCardStatusesE),
            url: z.string(),
            path_location: z.string(),
            reason: z.string().nullable()
        })
    }),
    z.null()
])

// Schemas for the API v3

export enum SignupSourceE {
    MORGIE = 'morgi',
    GOOGLE = 'google',
    FACEBOOK = 'facebook'
}

export const SignupSourceSchema = z.nativeEnum(SignupSourceE)

export const IdVerifiedSchemaV3 = z.union([
    z.object({
        cardIdStatus: z.nativeEnum(IdCardStatusesE),
        cardIdReason: z.string().nullable(),
        front: z.object({
            status: z.nativeEnum(IdCardStatusesE),
            url: z.string(),
            pathLocation: z.string(),
            reason: z.string().nullable()
        }),
        back: z.object({
            status: z.nativeEnum(IdCardStatusesE),
            url: z.string(),
            pathLocation: z.string(),
            reason: z.string().nullable()
        }),
        selfie: z.object({
            status: z.nativeEnum(IdCardStatusesE),
            url: z.string(),
            pathLocation: z.string(),
            reason: z.string().nullable()
        })
    }),
    z.null()
])
export const ProfileAlertSchema = z.object({code: z.nativeEnum(AlertTypeE), message: z.string()})
export const GeneralUserSchemaV3 = z.object({
    id: z.number(),
    signupSource: SignupSourceSchema.optional(),
    status: UserStatuses,
    signupCountry: CountrySchemaV3.optional(),
    type: z.nativeEnum(UserRoleE),
    fullName: z.string().optional(),
    username: z.string(),
    description: z.union([z.string(), z.null()]),
    email: z.string(),
    sentFirstMessage: z.boolean(),
    telegramBotToken: z.string(),
    rookieId: z.number().optional(),
    language: z.string().min(2),
    currency: z.string().min(3),
    referralCode: z.string(),
    persona: z.union([z.enum(['1', '2', '3', '4', '5', '6', '7']), z.null()]),
    joinedTelegramBotAt: z.union([z.string(), z.null()]).optional().nullable(),
    telegramChatId: z.union([z.number(), z.null()]).optional(),
    referredBy: z.union([z.string(), z.number(), z.null()]).optional(),
    isOnline: z.boolean(),
    createdAt: z.string().optional(),
    minMorgiToOpenConnection: z.number().optional(),
    hasNewCard: z.boolean().optional(),
    profileAlerts: z.array(ProfileAlertSchema).optional().nullable(), //DIEGO
    idVerified: IdVerifiedSchemaV3.optional(),
    preferredPaymentMethod: z
        .object({
            platform: z.nativeEnum(CreditCardTypeE),
            systemName: z.string(),
            gatewayType: z.string(),
            gatewayHash: z.string(),
            last4: z.union([z.number().nullable(), z.string().nullable()])
        })
        .nullable()
        .optional(),
    hasVerifiedId: z.boolean(),
    spokenLanguages: z.array(LanguageSchemaV3).optional(),
    genderId: z.number().nullable(),
    avatar: z
        .object({
            id: z.number(),
            createdAt: z.string().optional(),
            main: z.boolean(),
            pathLocation: z.string().optional(),
            underValidation: z.boolean().optional(),
            url: z.string().url(),
            userId: z.number().optional()
        })
        .nullable(),
    group: z
        .object({
            id: z.number(),
            name: z.string()
        })
        .nullable(),
    signupCountryId: z.number(),
    userCountryBlacklist: z.array(CountrySchemaV3).optional().nullable(),
    socials: z.array(SocialsSchemaV3).optional().nullable(),
    isBlocked: z.boolean().optional(),
    updatedAt: z.string().optional(),
    recentlyOnline: z.boolean(),
    ageVerifiedAt: z.union([z.date(), z.string()]).nullable(),
    isAgeVerified: z.boolean()
})

export type GeneralUserV3 = z.infer<typeof GeneralUserSchemaV3>

export const GeneralUserSchema = z.object({
    id: z.number(),
    signup_source: z.enum(['morgi', 'google', 'facebook']),
    status: UserStatuses,
    type: z.nativeEnum(UserRoleE),
    full_name: z.string(),
    username: z.string(),
    description: z.union([z.string(), z.null()]),
    email: z.string(),
    sent_first_message: z.boolean(),
    updated_username: z.union([z.string(), z.number()]),
    telegram_bot_token: z.string(),
    language: z.string().min(2),
    currency: z.string().min(3),
    referral_code: z.string(),
    persona: z.union([z.enum(['1', '2', '3', '4', '5', '6', '7']), z.null()]),
    advertisement_id: z.union([z.string(), z.null()]),
    joined_telegram_bot_at: z.union([z.string(), z.null()]),
    telegram_chat_id: z.union([z.number(), z.null()]),
    referred_by: z.union([z.string(), z.number(), z.null()]),
    is_online: z.boolean(),
    total_subscriptions_count: z.number(),
    total_successful_paused_connections: z.number(),
    total_paused_connections: z.number(),
    pubnub_uuid: z.string().uuid(),
    created_at: z.string(),
    min_morgi_to_open_connection: z.number().optional(),
    has_new_card: z.boolean().optional(),
    profile_alerts: z.array(ProfileAlertSchema).optional(),
    id_verified: IdVerifiedSchema,
    preferred_payment_method: z
        .object({
            platform: z.nativeEnum(CreditCardTypeE),
            system_name: z.string(),
            gateway_type: z.string(),
            gateway_hash: z.string(),
            last4: z.union([z.number().nullable(), z.string().nullable()])
        })
        .nullable()
        .optional(),
    treatcoin_id: z.union([z.number(), z.string(), z.null()]),
    chat_is_online: z.boolean(),
    has_verified_id: z.boolean(),
    spoken_languages: z.array(SpokenLanguagesSchema),
    gender: GenderSchema.nullable(),
    avatar: z
        .object({
            id: z.number(),
            created_at: z.string(),
            main: z.boolean(),
            path_location: z.string(),
            under_validation: z.boolean(),
            url: z.string().url(),
            user_id: z.number()
        })
        .nullable(),
    video: z
        .object({
            id: z.number(),
            user_id: z.number(),
            url: z.string(),
            path_location: z.string(),
            is_processed: z.number().optional(),
            created_at: z.string(),
            under_validation: z.boolean()
        })
        .nullable()
        .optional(),
    group: z.object({
        id: z.number(),
        name: z.string()
    }),
    signup_country: CountrySchema,
    pubnub_groups: z.array(
        z.object({
            id: z.number(),
            name: z.string(),
            user_id: z.number()
        })
    ),
    chat_topics: z.array(ChatTopicsSchema),
    user_country_blacklist: z.array(CountrySchema).optional().nullable(),
    favorite_activities: z.array(FavoriteActivitiesSchema),
    //TODO fix when user object will be fix
    socials: z.array(SocialsSchema),
    // socials: z.array(z.object({}))
    is_blocked: z.boolean().optional()
})

export const LoginPayloadSchema = z.object({
    email: z.string(),
    password: z.string()
})

export const HostSchema = GeneralUserSchema.extend({
    type_attributes: HostTypeAttributesScheme
})

export const HostSchemaV3 = GeneralUserSchemaV3.extend({
    typeAttributes: HostTypeAttributesSchemeV3
})

export const GuestSchemaV3 = GeneralUserSchemaV3.extend({
    typeAttributes: GuestTypeAttributesSchemaV3,
    nickname: z
        .array(
            z.object({
                id: z.number(),
                nickname: z.string(),
                nicknamerId: z.number(),
                nicknamedId: z.number(),
                createdAt: z.string(),
                updatedAt: z.string()
            })
        )
        .nullable()
        .optional(),
    isAgeVerified: z.boolean()
})

export const GuestSchema = GeneralUserSchema.extend({
    type_attributes: GuestTypeAttributesSchema,
    nickname: z
        .array(
            z.object({
                id: z.number(),
                nickname: z.string(),
                nicknamer_id: z.number(),
                nicknamed_id: z.number(),
                created_at: z.string(),
                updated_at: z.string()
            })
        )
        .nullable()
        .optional()
})

export const GuestProfileExtendedSchema = GuestSchema.extend({
    micro_morgi_given: z.number(),
    morgi_given: z.number()
})

export const RepresentativeTypeAttributesSchema = z
    .object({
        agent: HostsAgentSchema.nullable().optional(),
        agent_id: z.number().nullable(),
        email: z.string().nullable(),
        first_name: z.string(),
        id: z.number(),
        invitation_id: z.number(),
        last_name: z.string(),
        phone_number: z.string().nullable(),
        phone_number_country: z.string().nullable()
    })
    .extend({
        modes: z.array(z.object({id: z.number(), key_name: z.string()})),
        rookies: z.array(HostSchema.deepPartial())
    })

export const RepresentativeTypeAttributesSchemaV3 = z
    .object({
        agent: HostsAgentSchemaV3.nullable().optional(),
        agentId: z.number().nullable(),
        email: z.string().nullable().optional(),
        firstName: z.string().optional(),
        id: z.number(),
        invitationId: z.number().optional(),
        lastName: z.string(),
        phoneNumber: z.string().nullable(),
        phoneNumberCountry: z.string().nullable()
    })
    .extend({
        modes: z.array(z.object({id: z.number(), keyName: z.string()})),
        rookies: z.array(HostSchemaV3.deepPartial())
    })

export const AgentTypeAttributesSchema = z.object({
    email: z.string().nullable(),
    first_name: z.string(),
    id: z.number(),
    invitation_id: z.number(),
    last_name: z.string(),
    phone_number: z.string().nullable(),
    phone_number_country: z.string().nullable()
})

export const AgentTypeAttributesSchemaV3 = z.object({
    email: z.string().nullable().optional(),
    firstName: z.string(),
    id: z.number(),
    invitationId: z.number(),
    lastName: z.string(),
    phoneNumber: z.string().nullable(),
    phoneNumberCountry: z.string().nullable()
})

export const RepresentativeSchema = GeneralUserSchema.extend({
    type_attributes: RepresentativeTypeAttributesSchema
})

export const RepresentativeSchemaV3 = GeneralUserSchemaV3.extend({
    typeAttributes: RepresentativeTypeAttributesSchemaV3
})

export const AgentSchema = GeneralUserSchema.extend({
    type_attributes: AgentTypeAttributesSchema
})

export const AgentSchemaV3 = GeneralUserSchemaV3.extend({
    typeAttributes: AgentTypeAttributesSchemaV3
})

export const UserMeSchema = z.discriminatedUnion('userType', [
    z.object({userType: z.literal(UserRoleE.Host), data: HostSchemaV3}),
    z.object({userType: z.literal(UserRoleE.Guest), data: GuestSchemaV3}),
    z.object({
        userType: z.literal(UserRoleE.Representative),
        data: RepresentativeSchemaV3
    }),
    z.object({
        userType: z.literal(UserRoleE.Agent),
        data: AgentSchemaV3
    })
])

export const UserSchema = z.union([GuestSchema, HostSchema, RepresentativeSchema, AgentSchema])

export const UserSchemaV3 = z.union([GuestSchemaV3, HostSchemaV3, RepresentativeSchemaV3, AgentSchemaV3])

export const LoginSocialUserBodySchema = z.object({
    access_token: z.string().optional(),
    token_type: z.string().optional(),
    expires_in: z.number().optional(),
    scope: z.string().optional(),
    authuser: z.string().optional(),
    prompt: z.string().optional(),
    first_rookie: z.string().nullable().optional(),
    public_group: z.number().optional(),
    referral_code: z.string().nullable().optional(),
    path_id: z.string().nullable().optional(),
    persona: z.string().nullable().optional(),
    goal_id: z.string().nullable().optional(),
    tc_id: z.string().nullable().optional()
})

export const AccessTokenSchema = z.object({
    access_token: z.string(),
    token_type: z.string(),
    expires_at: z.string(), // Change this to the appropriate date type if needed
    expires_in_minutes: z.number(),
    is_first_login: z.boolean(),
    user: GuestSchema
})

export const GenderResponseSchema = z.array(GenderSchema)
export const GenderResponseSchemaV3 = z.array(GenderSchemaV3)

export const HostPartialSchema = HostSchema.deepPartial()
export const GuestPartialSchema = GuestSchema.deepPartial()

export const HostPartialSchemaV3 = HostSchemaV3.deepPartial()
export const GuestPartialSchemaV3 = GuestSchemaV3.deepPartial()

export const AttachSocialUserSchema = z.object({
    userID: z.string(),
    password: z.string(),
    platform: z.nativeEnum(SocialPlatformTypeE),
    data: GuestPartialSchema
})

export const RepresentedUserSchema = z.object({
    id: z.number(),
    username: z.string(),
    pubnubGroups: z.array(
        z.object({
            id: z.number(),
            name: z.string(),
            userId: z.number()
        })
    )
})

export const LoginResponseV3Schema = z.object({
    accessToken: z.string(),
    accessTokenV1: z.string(),
    user: GeneralUserSchemaV3
})

export type LoginResponseV3 = z.infer<typeof LoginResponseV3Schema>

export const FeaturedExpertise = z.object({
    id: z.number(),
    keyName: z.string(),
    category: z.string(),
    name: z.string()
})

export const FeaturedCreatorSchema = z.object({
    id: z.number(),
    rookieId: z.number(),
    featuredRookieId: z.number(),
    createdAt: z.string(),
    updatedAt: z.string(),
    fullName: z.string(),
    birthDate: z.string(),
    username: z.string(),
    path: HostPathSchema,
    description: z.string(),
    expertise: z.array(FeaturedExpertise),
    position: z.string(),
    photos: z.array(PhotoSchema),
    photosCount: z.number()
})
export const FeaturedSocials = z.object({
    id: z.number(),
    keyName: z.string(),
    baseUrl: z.string(),
    name: z.string()
})

export const FeaturedFollowerSchema = z.object({
    id: z.number(),
    signupSource: z.enum(['google', 'facebook', 'morgi', 'magicLink']),
    status: z.nativeEnum(UserStatusesE),
    type: z.nativeEnum(UserRoleE),
    fullName: z.string(),
    username: z.string(),
    description: z.string().nullable(),
    email: z.string(),
    sentFirstMessage: z.boolean(),
    telegramBotToken: z.string(),
    language: z.string().min(2),
    currency: z.string().min(3),
    referralCode: z.string(),
    persona: z.enum(['1', '2', '3']).nullable(),
    joinedTelegramBotAt: z.string().nullable(),
    telegramChatId: z.number().nullable(),
    referredBy: z.union([z.string(), z.number()]).nullable(),
    createdAt: z.string(),
    minMorgiToOpenConnection: z.number().optional(),
    profileAlerts: z.array(ProfileAlertSchema).optional(),
    idVerified: z.boolean(),
    hasVerifiedId: z.boolean(),
    spokenLanguages: z.array(LanguageSchemaV3),
    genderId: z.number().nullable(),
    avatar: PhotoSchema.nullable(),
    group: z.object({
        id: z.number(),
        name: z.string()
    }),
    signupCountryId: z.number(),
    userCountryBlacklist: z.array(CountrySchemaV3).nullable().optional(),
    socials: z.array(FeaturedSocials),
    isBlocked: z.boolean(),
    updatedAt: z.string(),
    recentlyOnline: z.boolean(),
    isOnline: z.boolean(),
    typeAttributes: HostTypeAttributesSchemeV3
})

export const FeaturedCreatorsRequestSchema = z.array(FeaturedCreatorSchema)
export const FeaturedFollowersRequestSchema = z.array(FeaturedFollowerSchema)
